import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Mic, Square, ArrowUp, Loader2 } from 'lucide-react';

import { useDictation } from '../../hooks/useDictation.js';

/**
 * Modern chat composer: auto-growing input, push-to-talk dictation, and a
 * send affordance. Replaces the plain textarea.
 */
export function Composer({ value, onChange, onSubmit, sending, disabled, language = 'it' }) {
  const ref = useRef(null);
  const baseRef = useRef('');
  const dictation = useDictation({ language });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  const submit = () => {
    if (sending || disabled) return;
    if (value.trim()) onSubmit();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const toggleDictation = () => {
    if (dictation.recording) {
      dictation.stop();
      return;
    }
    // Snapshot the current draft so live results are appended to it without
    // overwriting what the user already typed.
    baseRef.current = value ? `${value} ` : '';
    dictation.start((text) => onChange(`${baseRef.current}${text}`));
  };

  return (
    <div className="rounded-2xl border border-border-strong bg-surface-elevated p-2 shadow-soft">
      <textarea
        ref={ref}
        rows={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        style={{ outline: "none" }}
        placeholder="Scrivi un messaggio, oppure detta con il microfono..."
        className="max-h-[200px] w-full resize-none bg-transparent px-3 py-2 text-sm text-ink placeholder:text-ink-faint [outline:none] focus:[outline:none] focus-visible:[outline:none]"
      />
      <div className="flex items-center justify-between gap-2 px-1 pt-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleDictation}
            disabled={disabled || dictation.busy}
            aria-label={dictation.recording ? 'Ferma dettatura' : 'Detta messaggio'}
            className={clsx(
              'inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
              dictation.recording
                ? 'border-danger bg-danger/10 text-danger'
                : 'border-border-strong text-ink-muted hover:bg-surface-subtle hover:text-ink',
              (disabled || dictation.busy) && 'opacity-50'
            )}
          >
            {dictation.busy
              ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
              : dictation.recording
                ? <Square className="h-4 w-4 fill-current" strokeWidth={1.75} />
                : <Mic className="h-4 w-4" strokeWidth={1.75} />}
          </button>
          <span className="text-xs text-ink-faint">
            {dictation.recording ? 'In ascolto... tocca per fermare' : dictation.busy ? 'Trascrizione...' : 'Invio per inviare, Shift+Invio a capo'}
          </span>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={disabled || sending || !value.trim()}
          aria-label="Invia"
          className={clsx(
            'inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink text-white transition-colors hover:bg-brand-hover',
            (disabled || sending || !value.trim()) && 'cursor-not-allowed bg-ink-faint'
          )}
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <ArrowUp className="h-4 w-4" strokeWidth={2.25} />}
        </button>
      </div>
      {dictation.error && <p className="px-3 pb-1 pt-0.5 text-xs text-danger">{dictation.error}</p>}
    </div>
  );
}
